export const viewerTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export const ALL_TZ = (() => {
  try {
    const list = Intl.supportedValuesOf("timeZone");
    return list.includes("UTC") ? list : ["UTC", ...list];
  } catch (e) {
    return ["UTC", viewerTz()];
  }
})();

const fmt = (iso, tz, opts) => {
  try { return new Intl.DateTimeFormat(undefined, { timeZone: tz, ...opts }).format(new Date(iso)); } catch (e) { return ""; }
};

export const fmtDateTime = (iso, tz = viewerTz()) => fmt(iso, tz, { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
export const fmtDate = (iso, tz = viewerTz()) => fmt(iso, tz, { weekday: "short", day: "numeric", month: "short" });
export const fmtTime = (iso, tz = viewerTz()) => fmt(iso, tz, { hour: "numeric", minute: "2-digit" });

export const tzAbbr = (iso, tz = viewerTz()) => {
  try {
    const p = new Intl.DateTimeFormat(undefined, { timeZone: tz, timeZoneName: "short" }).formatToParts(new Date(iso));
    return p.find((x) => x.type === "timeZoneName")?.value || tz;
  } catch (e) { return tz; }
};

export const tzCity = (tz) => (tz || "").split("/").pop().replace(/_/g, " ");

export const fmtRange = (start, end, tz = viewerTz()) => {
  if (!start || !end) return "";
  const sameDay = fmtDate(start, tz) === fmtDate(end, tz);
  return sameDay ? `${fmtDate(start, tz)}, ${fmtTime(start, tz)} – ${fmtTime(end, tz)}` : `${fmtDate(start, tz)}, ${fmtTime(start, tz)} – ${fmtDate(end, tz)}, ${fmtTime(end, tz)}`;
};

export const fmtDuration = (ms) => {
  const m = Math.max(0, Math.round(ms / 60000));
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), r = m % 60;
  return [d && `${d}d`, h && `${h}h`, (r || (!d && !h)) && `${r}m`].filter(Boolean).join(" ");
};

export const fmtRelative = (iso, now = Date.now()) => {
  const diff = new Date(iso).getTime() - now;
  return diff >= 0 ? `in ${fmtDuration(diff)}` : `${fmtDuration(-diff)} ago`;
};

const tzOffset = (ms, tz) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })
    .formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second) - ms;
};

/** "2026-10-26T02:00" in a time zone -> Date (UTC instant). */
export const zonedToUtc = (local, tz) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local || "");
  if (!m) return null;
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  try {
    const first = guess - tzOffset(guess, tz);
    return new Date(guess - tzOffset(first, tz));
  } catch (e) { return null; }
};

/** Date -> "YYYY-MM-DD" and "HH:MM" in a time zone. */
export const utcToZonedParts = (date, tz) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    .formatToParts(date).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${String(+p.hour % 24).padStart(2, "0")}:${p.minute}` };
};
